import { X, ShoppingBag, Loader2, Trash2, Plus, Minus } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useCartStore } from '../../context/store'
import { formatPrice } from '../../utils/shopify'

export default function CartDrawer() {
  const { isOpen, closeCart, lines, subtotal, currency, loading, updateItem, removeItem, checkout } = useCartStore()

  return (
    <>
      {/* Backdrop */}
      {isOpen && <div className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm" onClick={closeCart} />}

      {/* Drawer */}
      <div className={`fixed right-0 top-0 h-full w-full max-w-md bg-brand-dark border-l border-white/10 z-50 flex flex-col transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <ShoppingBag size={20} className="text-brand-purple" />
            <h2 className="font-semibold">Cart ({lines.reduce((s, l) => s + l.quantity, 0)})</h2>
          </div>
          <button onClick={closeCart} className="p-1.5 hover:bg-brand-surface rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {lines.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-brand-muted">
              <ShoppingBag size={48} className="opacity-20" />
              <p>Your cart is empty</p>
              <Link to="/catalogue" onClick={closeCart} className="btn-primary px-6 py-2 text-sm">
                Browse Products
              </Link>
            </div>
          ) : (
            lines.map(line => (
              <div key={line.id} className="flex gap-3 bg-brand-surface rounded-xl p-3">
                {line.image && (
                  <img src={line.image} alt={line.title} className="w-16 h-16 object-cover rounded-lg flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{line.title}</p>
                  {line.variantTitle && line.variantTitle !== 'Default Title' && (
                    <p className="text-xs text-brand-muted">{line.variantTitle}</p>
                  )}
                  <p className="text-brand-purple text-sm font-semibold mt-1">{formatPrice(line.price, currency)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => updateItem(line.variantId, line.quantity - 1)}
                      disabled={loading}
                      className="w-6 h-6 rounded bg-brand-dark flex items-center justify-center hover:bg-brand-purple/20 transition-colors disabled:opacity-50"
                    >
                      <Minus size={12} />
                    </button>
                    <span className="text-sm w-5 text-center">{line.quantity}</span>
                    <button
                      onClick={() => updateItem(line.variantId, line.quantity + 1)}
                      disabled={loading}
                      className="w-6 h-6 rounded bg-brand-dark flex items-center justify-center hover:bg-brand-purple/20 transition-colors disabled:opacity-50"
                    >
                      <Plus size={12} />
                    </button>
                    <button
                      onClick={() => removeItem(line.variantId)}
                      disabled={loading}
                      className="ml-auto p-1 hover:text-red-400 transition-colors disabled:opacity-50"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {lines.length > 0 && (
          <div className="p-4 border-t border-white/10 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-brand-muted text-sm">Subtotal</span>
              <span className="font-semibold">{formatPrice(subtotal, currency)}</span>
            </div>
            <p className="text-xs text-brand-muted">Shipping and taxes calculated at checkout</p>
            <button
              onClick={checkout}
              disabled={loading}
              className="w-full btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              Checkout
            </button>
          </div>
        )}
      </div>
    </>
  )
}
